import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Globe, ShoppingCart, Package, Users, IndianRupee, PlusCircle, ArrowRight, AlertTriangle, Sparkles } from "lucide-react";
import api, { fmtINR, fmtDateTime } from "../../lib/api";
import { StatCard, PageHeader, StatusBadge, TableShell, TR, TD, LoadingRows } from "../../components/Shared";
import { PricingCards, PricingCalculator, computePlan } from "../../components/PricingPlans";
import { Button } from "../../components/ui/button";
import { useAuth } from "../../context/AuthContext";

export default function PartnerDashboard() {
  const [stats, setStats] = useState(null);
  const [pricing, setPricing] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/partner/stats").then((r) => setStats(r.data));
    api.get("/public/pricing").then((r) => setPricing(r.data.pricing));
  }, []);
  if (!stats) return <LoadingRows rows={5} />;

  const sub = stats.subscription_status;
  const warn = ["payment_due", "grace", "suspended"].includes(sub);

  return (
    <div data-testid="partner-dashboard">
      <PageHeader title={`Hello, ${user.name?.split(" ")[0]}`} subtitle="Here's how your client websites are performing.">
        <Button data-testid="create-website-cta" onClick={() => navigate("/partner/websites/new")}
          className="rounded-full font-bold h-12 px-6 text-base active:scale-95 transition-transform">
          <PlusCircle className="h-5 w-5 mr-2" /> Create New Website
        </Button>
      </PageHeader>

      {/* Subscription banner */}
      <div data-testid="subscription-banner" className={`mb-8 border rounded-md p-6 flex flex-wrap items-center gap-4 ${warn ? "border-amber-500/30 bg-amber-500/5" : "border-emerald-500/20 bg-emerald-500/5"}`}>
        <div className={`h-11 w-11 rounded-full flex items-center justify-center ${warn ? "bg-amber-500/10 text-amber-600" : "bg-emerald-500/10 text-emerald-600"}`}>
          {warn ? <AlertTriangle className="h-5 w-5" /> : <Sparkles className="h-5 w-5" />}
        </div>
        <div className="flex-1 min-w-52">
          <p className="font-display font-bold">
            {sub === "trial" ? `Free trial${stats.trial_ends_at ? ` · ends ${fmtDateTime(stats.trial_ends_at).split(",")[0]}` : ""}` :
             sub === "active" ? "Subscription active" :
             sub === "payment_due" ? "Payment due" : sub === "grace" ? "Grace period — pay soon" : "Subscription suspended"}
          </p>
          <p className="text-sm text-muted-foreground" data-testid="subscription-plan-line">
            {stats.active_websites} active websites · {computePlan(pricing, stats.active_websites).name} package · {fmtINR(stats.monthly_amount)}/month
          </p>
        </div>
        <Button data-testid="go-to-billing" variant="outline" onClick={() => navigate("/partner/billing")} className="rounded-full font-bold">
          View Billing <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
        <StatCard icon={Globe} label="Active Websites" value={`${stats.active_websites} / ${stats.total_websites}`} sub={stats.suspended_websites ? `${stats.suspended_websites} suspended` : "All healthy"} testid="stat-websites" />
        <StatCard icon={IndianRupee} label="Monthly Billing" value={fmtINR(stats.monthly_amount)} sub="Auto-calculated per site" testid="stat-billing" delay={0.05} />
        <StatCard icon={ShoppingCart} label="Orders" value={stats.orders} sub={fmtINR(stats.revenue) + " revenue"} testid="stat-orders" delay={0.1} />
        <StatCard icon={Package} label="Products" value={stats.products} testid="stat-products" delay={0.15} />
        <StatCard icon={Users} label="Customers" value={stats.customers} sub="Across all stores" testid="stat-customers" delay={0.2} />

        <div data-testid="recent-orders-card" className="md:col-span-3 lg:col-span-4">
          <div className="flex items-center justify-between mb-4">
            <span className="overline-label">Recent Orders Across Your Stores</span>
            <Link to="/partner/websites" className="text-sm font-bold hover:underline flex items-center gap-1">All websites <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
          {stats.recent_orders.length === 0 ? (
            <div className="border border-dashed border-border rounded-md p-12 text-center text-sm text-muted-foreground">
              No orders yet — they appear here the moment customers buy from your client stores.
            </div>
          ) : (
            <TableShell testid="recent-orders-table" head={["Order", "Store", "Customer", "Total", "Payment", "Status", "Placed"]}>
              {stats.recent_orders.map((o) => (
                <TR key={o.id} testid={`recent-order-${o.id}`}>
                  <TD className="font-mono text-xs font-bold">{o.order_number}</TD>
                  <TD className="text-sm">{o.website_name}</TD>
                  <TD className="text-sm">{o.customer?.name}</TD>
                  <TD className="font-bold">{fmtINR(o.total)}</TD>
                  <TD><StatusBadge status={o.payment_status} /></TD>
                  <TD><StatusBadge status={o.status} /></TD>
                  <TD className="text-xs text-muted-foreground">{fmtDateTime(o.created_at)}</TD>
                </TR>
              ))}
            </TableShell>
          )}
        </div>
      </div>

      {/* ===== SUBSCRIPTION PACKAGES ===== */}
      <div className="mt-12" data-testid="partner-pricing-section">
        <p className="overline-label">Subscription packages</p>
        <h2 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight mt-2">Simple pricing that scales with your agency</h2>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Billing counts only your active websites and updates automatically whenever that number changes.
          No payment is due right now — the payment gateway goes live in a later phase.
        </p>
        <div className="mt-6"><PricingCards cfg={pricing} currentKey={computePlan(pricing, stats.active_websites).key} compact /></div>
        <div className="mt-6"><PricingCalculator cfg={pricing} initial={stats.active_websites || 10} testidPrefix="dash-calc" /></div>
      </div>
    </div>
  );
}
